import DataTableTeamKnowledgeWebsites from "@/components/data-table-team-knowledge-websites/data-table-team-knowledge-websites";
import DialogTeamKnowledgeWebsite from "@/components/team/dialog-team-knowledge-website";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamKnowledgeWebsiteDTO } from "@shaarp/common";
import { Loader2Icon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";

export default function CardTeamKnowledgeWebsite() {
  const toastError = useToastError();
  const { t } = useTranslation();

  const api = useApi();

  const [selectedWebsite, setSelectedWebsite] =
    useState<TeamKnowledgeWebsiteDTO | null>(null);
  const [websites, setWebsites] = useState<TeamKnowledgeWebsiteDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddWebsiteDialog, setShowAddWebsiteDialog] =
    useState<boolean>(false);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { data: websites } = await api.get<TeamKnowledgeWebsiteDTO[]>(
        "/my/team/knowledge-base/websites"
      );
      setWebsites(websites);
    } catch (error) {
      setWebsites([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onRemoveWebsite = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.delete<TeamKnowledgeWebsiteDTO[]>(
        `/my/team/knowledge-base/websites/${selectedWebsite!.id}`
      );
      setWebsites(data);
      toast({
        variant: "default",
        title: t("team.knowledge.deleteWebsiteSuccess.title"),
        description: t("team.knowledge.deleteWebsiteSuccess.description", {
          name: selectedWebsite!.url,
        }),
      });
    } catch (error) {
      toastError();
    } finally {
      setSelectedWebsite(null);
      setIsLoading(false);
    }
  };

  const handleDialogAddWebsite = () => {
    fetchData();
  };

  const onRefreshWebsite = async (website: TeamKnowledgeWebsiteDTO) => {
    try {
      setIsLoading(true);
      const { data } = await api.post<TeamKnowledgeWebsiteDTO[]>(
        `/my/team/knowledge-base/websites/${website.id}/refresh`
      );
      setWebsites(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between">
            <div>
              <CardTitle>{t("team.knowledge.websites.title")}</CardTitle>
              <CardDescription>
                {t("team.knowledge.websites.description")}
              </CardDescription>
            </div>
            <div>
              <Button
                onClick={() => setShowAddWebsiteDialog(true)}
                disabled={isLoading}
              >
                <PlusIcon className="mr-2 size-4" />
                {t("team.knowledge.addWebsite")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTableTeamKnowledgeWebsites
            websites={websites}
            isLoading={isLoading}
            onDelete={(website) => setSelectedWebsite(website)}
            onRefresh={(website) => onRefreshWebsite(website)}
          />
        </CardContent>
      </Card>

      <DialogTeamKnowledgeWebsite
        open={showAddWebsiteDialog}
        onClose={() => setShowAddWebsiteDialog(false)}
        onAdd={handleDialogAddWebsite}
      />

      <AlertDialog open={selectedWebsite !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("team.dialog.knowledge.delete.title", {
                name: selectedWebsite?.url,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("team.dialog.knowledge.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setSelectedWebsite(null)}
              disabled={isLoading}
            >
              {t("listing.dialog.update.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRemoveWebsite();
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <Loader2Icon className="mr-2 animate-spin size-4" />
              )}
              {t("listing.dialog.update.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
